import {Component, Input, OnInit} from '@angular/core';
import {PermissionService} from '../../services/permission/permission.service';
import {SideMenuPermissionEnum, UserPermissionEnum} from '../../../shared/enums/permission.enum';
import {UsersService} from '../../http/users/users.service';
import {environment} from '../../../../environments/environment';
import {SATELLIZER_TOKEN, SATELLIZER_TOKEN_REMEMBER, YOPA_USER} from '../../constants/local-storage-keys.constansts';

interface INavigation {
  title: string;
  fontAwesomeClass?: string;
  url: string;
  isExternalLink?: boolean;
  e2eId?: string;
  permissions: Array<string> | null;
  notification?: {
    name: string;
    e2eId: string;
  };
  submenu?: INavigation[];
  isOpen?: boolean; // Status of dropdown
}

const OffersNotification = 'totalOffers';
const ViewingsNotification = 'totalPendingViewingRequest';
const MessagesNotification = 'totalUnreadMessages';
const TaskNotification = 'totalUncompletedTask';

interface Notifications {
  [OffersNotification]: number;
  [ViewingsNotification]: number;
  [MessagesNotification]: number;
  [TaskNotification]: number;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  @Input() sidebarStatus: boolean;

  public notifications: Notifications = {
    totalOffers: 0,
    totalPendingViewingRequest: 0,
    totalUnreadMessages: 0,
    totalUncompletedTask: 0,
  };

  public navigation: INavigation[] = [];

  constructor(private permissions: PermissionService,
              private users: UsersService) {
  }

  ngOnInit(): void {
    this.loadNotifications();
    this.generateNavigationObject();
  }

  generateNavigationObject(): void {
    this.navigation = [
      {
        title: 'Home',
        fontAwesomeClass: 'fa fa-tachometer',
        url: '/yopaplus/pcdashboard',
        isExternalLink: true,
        e2eId: 'home',
        permissions: [SideMenuPermissionEnum.ADMIN_DASHBOARD]
      },
      {
        title: 'Dashboard',
        fontAwesomeClass: 'fa fa-tachometer',
        url: '/yopaplus/dashboard',
        isExternalLink: true,
        e2eId: 'home',
        permissions: [SideMenuPermissionEnum.DASHBOARD]
      },
      {
        title: 'Overview',
        fontAwesomeClass: 'fa fa-tachometer',
        url: '/yopaplus/lpedashboard/overview///', // Slashed at the end is not a mistake...
        isExternalLink: true,
        e2eId: 'overview',
        permissions: [SideMenuPermissionEnum.OVERVIEW]
      },
      {
        title: 'League Tables',
        fontAwesomeClass: 'fa fa-list-ol',
        url: 'league-table',
        isExternalLink: false,
        e2eId: 'league-tables',
        permissions: [SideMenuPermissionEnum.LEAGUE_TABLES]
      },
      {
        title: 'Associate Properties',
        fontAwesomeClass: 'fa fa-home',
        url: 'associates/properties/list',
        isExternalLink: false,
        e2eId: 'associate-properties-list',
        permissions: [SideMenuPermissionEnum.ASSOCIATE_PROPERTY_LIST],
      },
      {
        title: 'Properties',
        fontAwesomeClass: 'fa fa-home',
        url: this.propertiesUrlLogic(),
        isExternalLink: true,
        e2eId: 'properties',
        permissions: [SideMenuPermissionEnum.PROPERTY]
      },
      {
        title: 'Associate Sellers approvals list',
        fontAwesomeClass: 'fa fa-hourglass',
        url: 'associates/properties/approvals/list',
        isExternalLink: false,
        e2eId: 'approvals-list',
        permissions: [SideMenuPermissionEnum.ASSOCIATE_PROPERTY_APPROVAL_LIST],
      },
      {
        title: 'Approvals list',
        fontAwesomeClass: 'fa fa-hourglass',
        url: 'approvals/list',
        isExternalLink: false,
        e2eId: 'approvals-list',
        permissions: [SideMenuPermissionEnum.PROPERTY_APPROVAL]
      },
      {
        title: 'Buyers approvals list',
        fontAwesomeClass: 'fa fa-hourglass',
        url: 'buyers-approvals',
        isExternalLink: false,
        e2eId: 'buyers-approvals',
        permissions: [UserPermissionEnum.USER_BUYERS_APPROVALS_LIST]
      },
      {
        title: 'Associate Buyers approvals list',
        fontAwesomeClass: 'fa fa-hourglass',
        url: 'associates/buyers-approvals',
        isExternalLink: false,
        e2eId: 'associate-buyers-approvals',
        permissions: [SideMenuPermissionEnum.ASSOCIATE_BUYER_APPROVAL_LIST],
      },
      {
        title: 'Messages',
        fontAwesomeClass: 'fa fa-comments',
        url: '/yopaplus/messages',
        isExternalLink: true,
        e2eId: 'messages',
        notification: {
          name: MessagesNotification,
          e2eId: 'messages-counter'
        },
        permissions: [SideMenuPermissionEnum.MESSAGES]
      },
      {
        title: 'Calendar',
        fontAwesomeClass: 'fa fa-calendar',
        url: '/yopaplus/calendar/0',
        isExternalLink: true,
        e2eId: 'calendar',
        permissions: [SideMenuPermissionEnum.CALENDAR]
      },
      {
        title: 'Property search',
        fontAwesomeClass: 'far fa-search',
        url: '/properties',
        e2eId: 'property-search',
        permissions: [SideMenuPermissionEnum.PROPERTY_SEARCH]
      },
      {
        title: 'Viewings',
        fontAwesomeClass: 'fas fa-eye',
        url: this.viewingsUrlLogic(),
        isExternalLink: this.viewingsUrlLogic().startsWith('/yopaplus'),
        e2eId: 'viewings',
        notification: {
          name: ViewingsNotification,
          e2eId: 'viewings-counter',
        },
        permissions: [
          SideMenuPermissionEnum.VIEWINGS,
          SideMenuPermissionEnum.VIEWINGS_REGIONAL_DIRECTOR,
        ]
      },
      {
        title: 'Tasks',
        fontAwesomeClass: 'fa fa-tasks',
        url: '/yopaplus/tasks/outstanding',
        isExternalLink: true,
        e2eId: 'tasklist',
        notification: {
          name: TaskNotification,
          e2eId: 'tasks-overdue-task-counter',
        },
        permissions: [SideMenuPermissionEnum.TASKS]
      },
      {
        title: 'Agents\' Tasks',
        fontAwesomeClass: 'fa fa-tasks',
        url: '/yopaplus/regionaltasks/overview',
        isExternalLink: true,
        e2eId: 'agents-tasklist',
        permissions: [SideMenuPermissionEnum.AGENT_TASK]
      },
      {
        title: 'Tasks',
        fontAwesomeClass: 'fa fa-tasks',
        url: '/yopaplus/tse/overview',
        isExternalLink: true,
        e2eId: 'tse-tasklist',
        permissions: [SideMenuPermissionEnum.TSE_TASK]
      },
      {
        title: 'Feedback',
        fontAwesomeClass: 'fas fa-comment-dots',
        url: '/yopaplus/feedback-v2',
        isExternalLink: true,
        e2eId: 'feedback',
        permissions: [SideMenuPermissionEnum.FEEDBACK]
      },
      {
        title: 'Offers',
        fontAwesomeClass: 'far fa-money-bill-alt',
        url: this.offersUrlLogic(),
        isExternalLink: true,
        e2eId: 'offers',
        notification: {
          name: OffersNotification,
          e2eId: 'offers-counter',
        },
        permissions: [SideMenuPermissionEnum.OFFERS]
      },
      {
        title: 'Yopa Services',
        fontAwesomeClass: 'far fa-store-alt',
        url: '/yopaplus/yopa-services',
        isExternalLink: true,
        e2eId: 'yopa-services',
        permissions: [SideMenuPermissionEnum.YOPA_SERVICES]
      },
      {
        title: 'Buyers',
        fontAwesomeClass: 'far fa-user-ninja',
        url: '/buyers',
        e2eId: 'buyers',
        permissions: [SideMenuPermissionEnum.BUYERS]
      },
      {
        title: 'Users',
        fontAwesomeClass: 'fa fa-users',
        url: '/users/list',
        e2eId: 'users',
        permissions: [SideMenuPermissionEnum.USERS]
      },
      {
        // Branch Buyers list
        title: 'Buyers',
        fontAwesomeClass: 'far fa-user-ninja',
        url: '/buyers/list',
        e2eId: 'buyers',
        permissions: [SideMenuPermissionEnum.BRANCH_BUYER_LIST]
      },
      {
        title: 'Associate Users',
        fontAwesomeClass: 'fa fa-users',
        url: '/associates/users/list',
        e2eId: 'users',
        permissions: [SideMenuPermissionEnum.ASSOCIATE_USER_LIST],
      },
      {
        title: 'Kickers',
        fontAwesomeClass: 'fa fa-envelope',
        url: '/yopaplus/kickers/list',
        isExternalLink: true,
        e2eId: 'kickers',
        permissions: [SideMenuPermissionEnum.KICKERS]
      },
      {
        title: 'Referrals',
        fontAwesomeClass: 'fa fa-th-list',
        url: 'product-referrals/userlist',
        isExternalLink: false,
        e2eId: 'product-referrals',
        permissions: [SideMenuPermissionEnum.REFERRALS]
      },
      {
        title: 'Promotions',
        fontAwesomeClass: 'fa fa-tags',
        url: '/yopaplus/promotions/list',
        isExternalLink: true,
        e2eId: 'promotions',
        permissions: [SideMenuPermissionEnum.PROMOTIONS]
      },
      {
        title: 'Mortgages',
        fontAwesomeClass: 'fas fa-pound-sign',
        url: '/yopaplus/mortgages/info',
        isExternalLink: true,
        e2eId: 'selfReferrals',
        permissions: [SideMenuPermissionEnum.MORTGAGES]
      },
      {
        title: 'Conveyancing',
        fontAwesomeClass: 'fa fa-home',
        url: '/yopaplus/conveyancing/info',
        isExternalLink: true,
        e2eId: 'conveyancing',
        permissions: [SideMenuPermissionEnum.CONVEYANCING]
      },
      {
        title: 'My Profile',
        fontAwesomeClass: 'far fa-smile',
        url: '/yopaplus/profile/information',
        isExternalLink: true,
        e2eId: 'my-profile',
        permissions: [SideMenuPermissionEnum.MY_PROFILE]
      },
      {
        title: 'Book Valuation',
        fontAwesomeClass: 'fa fa-home',
        url: '/yopaplus/book-valuation',
        isExternalLink: true,
        e2eId: 'book-valuation',
        permissions: [SideMenuPermissionEnum.BOOK_VALUATION]
      },
      {
        title: 'Tools',
        fontAwesomeClass: 'fa fa-cogs',
        url: undefined,
        e2eId: 'tools-main-item',
        permissions: [
          SideMenuPermissionEnum.TOOLS,
          SideMenuPermissionEnum.BRANCHE_OUTCOUDE_LIST,
          SideMenuPermissionEnum.TOOLS_BRANCHES_LIST,
          SideMenuPermissionEnum.TOOLS_ASSOCIATE_LIST,
          SideMenuPermissionEnum.MISSING_POSTCODES
        ],
        isOpen: false,
        submenu: [
          {
            title: 'Associates',
            url: '/tools/associates',
            e2eId: 'tools-associates',
            permissions: [SideMenuPermissionEnum.TOOLS_ASSOCIATE_LIST]
          },
          {
            title: 'Users',
            url: '/tools/users',
            e2eId: 'tools-users',
            permissions: [SideMenuPermissionEnum.TOOLS]
          },
          {
            title: 'Outcodes',
            url: '/tools/outcodes/live',
            e2eId: 'tools-outcodes',
            permissions: [SideMenuPermissionEnum.TOOLS]
          },
          {
            title: 'Missing postcodes',
            url: '/tools/missing-postcodes',
            e2eId: 'tools-missing-postcodes',
            permissions: [SideMenuPermissionEnum.MISSING_POSTCODES]
          },
          {
            title: 'Branch',
            url: '/tools/branch/outcodes/permanent',
            e2eId: 'tools-branch-outcodes',
            permissions: [SideMenuPermissionEnum.BRANCHE_OUTCOUDE_LIST]
          },
          {
            title: 'Branches',
            url: '/tools/branches',
            e2eId: 'tools-branches',
            permissions: [SideMenuPermissionEnum.TOOLS, SideMenuPermissionEnum.TOOLS_BRANCHES_LIST]
          },
          {
            title: 'Team Members',
            url: '/tools/team-members',
            e2eId: 'tools-team-members',
            permissions: [SideMenuPermissionEnum.TOOLS]
          },
          {
            title: 'Pages',
            url: '/tools/pages',
            e2eId: 'tools-pages',
            permissions: [SideMenuPermissionEnum.TOOLS]
          },
          {
            title: 'Property Transfer',
            url: '/tools/property-transfer',
            e2eId: 'tools-property-transfer',
            permissions: [SideMenuPermissionEnum.TOOLS]
          },
          {
            title: 'CDN Assets',
            url: '/tools/cdn-assets',
            e2eId: 'tools-cdn-assets',
            permissions: [SideMenuPermissionEnum.TOOLS_CDN_ASSETS]
          },
        ]
      }
    ];
  }

  getMenuItems(): INavigation[] {
    const mainNav = this.filterPermissionMenu(this.navigation);
    return mainNav.map((nav) => {
      if (nav.submenu) {
        nav.submenu = this.filterPermissionMenu(nav.submenu);
      }
      return nav;
    });
  }

  toggleDropdown(index: number): void {
    this.getMenuItems()[index].isOpen = !this.getMenuItems()[index].isOpen;
  }

  private filterPermissionMenu(navList): Array<INavigation> {
    return navList.filter((nav) => {
      if (!Array.isArray(nav.permissions)) {
        return true;
      } else {
        return this.permissions.hasOneOfPermissions({permissions: nav.permissions});
      }
    });
  }

  /**
   * Loads user notifications
   */
  private loadNotifications(): void {
    this.users.getUserToDo().subscribe((data) => {
      this.notifications.totalOffers = data.offers.total;
      this.notifications.totalPendingViewingRequest = data.pendingViewingRequest;
      this.notifications.totalUnreadMessages = data.totalUnreadMessages;
      this.notifications.totalUncompletedTask = data.totalUncompletedTask;
    });
  }

  /**
   * Permission based url change for Properties tab
   */
  private propertiesUrlLogic(): string {
    const isBuyer = this.permissions.hasPermission(SideMenuPermissionEnum.PROPERTY_BUYER);
    const isSeller = this.permissions.hasPermission(SideMenuPermissionEnum.PROPERTY_CUSTOMER);
    let url = '/yopahub/properties/list';

    if (isBuyer && !isSeller) {
      url = '/yopaplus/properties/list/buying';
    } else if (isSeller) {
      url = '/yopaplus/properties/list/selling';
    }

    return url;
  }

  /**
   * Permission based url change for Viewings tab
   */
  private viewingsUrlLogic(): string {
    const isRegionalDirector = this.permissions.hasPermission(SideMenuPermissionEnum.VIEWINGS_REGIONAL_DIRECTOR);
    const isBuyer = this.permissions.hasPermission(SideMenuPermissionEnum.VIEWINGS_BUYER);
    const isSeller = this.permissions.hasPermission(SideMenuPermissionEnum.VIEWINGS_VENDOR);
    const isAgent = this.permissions.hasPermission(SideMenuPermissionEnum.VIEWINGS_AGENT);
    const isBranchAdmin = this.permissions.hasPermission(SideMenuPermissionEnum.VIEWINGS_BRANCH_ADMINISTRATOR);
    let url = '/yopaplus/viewings/selling';

    if (isRegionalDirector) {
      url = 'viewings/regional-director';
    } else if (isBuyer && !isSeller) {
      url = '/yopaplus/viewings/buying';
    } else if (isAgent || isBranchAdmin) {
      url = '/yopaplus/viewings/lpe';
    }

    return url;
  }

  /**
   * Permission based url change for Offers tab
   */
  private offersUrlLogic(): string {
    const isBuyer = this.permissions.hasPermission(SideMenuPermissionEnum.OFFERS_BUYER);
    const isSeller = this.permissions.hasPermission(SideMenuPermissionEnum.OFFERS_VENDOR);
    let url = '/yopaplus/offers/properties/delegated';

    if (isSeller) {
      url = '/yopaplus/offers/properties/my-properties';
    } else if (isBuyer) {
      url = '/yopaplus/offers/properties/want-to-buy';
    }

    return url;
  }

  /**
   * Generate url with token for legacy tools
   */
  private generateToolsLegacyUrl(): string {
    const userDetails = JSON.parse(JSON.parse(localStorage.getItem(YOPA_USER)));
    const info = {
      name: userDetails.name,
      roles: userDetails.roles.map((entry) => entry.name),
      token: localStorage.getItem(SATELLIZER_TOKEN),
      rememberToken: localStorage.getItem(SATELLIZER_TOKEN_REMEMBER)
    };
    return environment.toolsUrl + window.btoa(JSON.stringify(info));
  }

}
